import { FormattedMessage, FormattedNumber } from 'react-intl';

import type { StickerGradient } from '#src/atoms/ui/Sticker/index.tsx';
import { Sticker } from '#src/atoms/ui/Sticker/index.tsx';

const BOOST_SHAPE =
  'm12.081 7.8949h.3843.0024v-2.34422l3.5323 4.3847v6.14102l-4.9995 4.9113-1.75301-6.8783h-2.59823l-1.94987 6.8906h-.10326l-4.59613-4.5611v-11.37323l4.11346 3.14277h.51148v-8.20844z';

/** Custom percent sign, drawn in a 6x6 box starting at (0.5, 0.5) */
const PERCENT_SIGN =
  'M3.04546 1.77273C3.04546 1.07143 2.47403 0.5 1.77273 0.5C1.07143 0.5 0.5 1.07143 0.5 1.77273C0.5 2.47403 1.07143 3.04546 1.77273 3.04546C2.47403 3.04546 3.04546 2.47403 3.04546 1.77273ZM0.88961 6.42208C0.98052 6.42208 1.08442 6.38312 1.16234 6.3052L6.29221 1.17532C6.42208 1.03247 6.42208 0.785714 6.27922 0.642857C6.13637 0.5 5.88961 0.5 5.74676 0.642857L0.616883 5.77273C0.474026 5.91559 0.474026 6.16234 0.616883 6.3052C0.694805 6.38312 0.785714 6.42208 0.88961 6.42208ZM3.95455 5.22727C3.95455 5.92857 4.52598 6.5 5.22727 6.5C5.92857 6.5 6.5 5.92857 6.5 5.22727C6.5 4.52598 5.92857 3.95455 5.22727 3.95455C4.52598 3.95455 3.95455 4.52598 3.95455 5.22727Z';
const PERCENT_SIGN_WIDTH = 6;
const PERCENT_SIGN_GAP = 1;

const BOOST_GRADIENT: StickerGradient = {
  coords: { x1: '0px', y1: '21px', x2: '0px', y2: '5.25px' },
  stops: (
    <>
      <stop stopColor="#EAE3DA" />
      <stop offset="0.283178" stopColor="#BFE6EF" />
      <stop offset="0.623627" stopColor="#E1E3F5" />
      <stop offset="0.811813" stopColor="#E6BADD" />
      <stop offset="1" stopColor="#E8F6EF" />
    </>
  ),
};
const BIG_BOOST_GRADIENT: StickerGradient = {
  coords: { x1: '8.8px', y1: '21px', x2: '7.2px', y2: '5.25px' },
  stops: (
    <>
      <stop stopColor="#FEF314" />
      <stop offset="1" stopColor="#FF4E22" />
    </>
  ),
};

type Props = { percent: number; className?: string };
export const BoostSticker = ({ percent, className }: Props) => {
  if (!percent) {
    return null;
  }

  return (
    <Sticker
      className={className}
      shape={BOOST_SHAPE}
      viewBox="0 0 16 21"
      height={24}
      fitBoxToPaintedContent
      fill={percent >= 0.1 ? BIG_BOOST_GRADIENT : BOOST_GRADIENT}
      textColor="var(--c-black)"
      textOutlineWidth={3}
      title={
        <FormattedMessage
          defaultMessage="{value} boost"
          description="Card boost sticker aria label"
          values={{
            value: (
              <FormattedNumber
                value={percent}
                style="percent"
                signDisplay="always"
              />
            ),
          }}
        />
      }
    >
      <g transform="translate(8 11)">
        <g style={{ transformBox: 'fill-box', transform: 'translateX(50%)' }}>
          <text
            x={-(PERCENT_SIGN_WIDTH + PERCENT_SIGN_GAP)}
            y="0"
            textAnchor="end"
            dominantBaseline="central"
            fontSize="12"
            fontWeight="500"
            letterSpacing="-0.2"
          >
            <FormattedNumber value={percent * 100} signDisplay="always" />
          </text>
          <path
            transform={`translate(${-PERCENT_SIGN_WIDTH - 0.5} ${-PERCENT_SIGN_WIDTH / 2 - 0.5})`}
            d={PERCENT_SIGN}
          />
        </g>
      </g>
    </Sticker>
  );
};
